import { useEffect } from "react";
import Lenis from "@studio-freight/lenis";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * Site-wide smooth scroll + ScrollTrigger sync for the marketing pages.
 *
 * Separate from the legacy SmoothScroll.jsx (which is tangled up with the
 * /experience page's INTRO/MAP phase machine). This one is plain: start
 * Lenis, drive ScrollTrigger from it, and clean everything up on unmount.
 *
 * Skips entirely under prefers-reduced-motion — native scroll + the CSS
 * fallbacks in each scene handle that case.
 */
export default function SiteSmoothScroll() {
  useEffect(() => {
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.6,
    });
    window.__lenis = lenis;

    lenis.on("scroll", ScrollTrigger.update);

    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    // Let scenes that lazy-mount (Suspense) or images that load late
    // recompute their trigger bounds — otherwise triggers are measured
    // against a too-short page and never fire.
    const onRefresh = () => { try { ScrollTrigger.refresh(); } catch (_) {} };
    window.addEventListener("load", onRefresh);
    const timers = [300, 800, 1600, 2600].map((ms) => setTimeout(onRefresh, ms));

    // Refresh as each already-in-DOM image finishes loading.
    const imgs = Array.from(document.images || []);
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", onRefresh, { once: true });
    });

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("load", onRefresh);
      imgs.forEach((img) => img.removeEventListener("load", onRefresh));
      gsap.ticker.remove(tick);
      lenis.destroy();
      delete window.__lenis;
    };
  }, []);

  return null;
}
